import * as React from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import axios from "axios";
import { AccountsURL } from "api/axios.config";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import { toast } from "react-toastify";
import { getChitImportTypes } from "api/accountsAPI";
import { RiFileAddFill } from "react-icons/ri";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { DialogActionsBar } from "@progress/kendo-react-dialogs";
import SubmitButton from "components/common/SubmitButton";
import { AmountFormatCell, DateFormatCell } from "components/common/formatter";
import styles from "./ImportChits.module.scss";

// Custom cell component for validation fields
const ValidationCell = (props) => {
    const message = props.dataItem[props.field];
    const isValid = props.dataItem.isValid;

    const displayMessage = !isValid && (!message || message.trim() === "")
        ? "Invalid"
        : message;

    return (
        <td className={styles.validationText}>
            {displayMessage}
        </td>
    );
};

// Custom cell component for boolean isValid field
const BooleanValidationCell = (props) => {
    const value = props.dataItem[props.field];
    return (
        <td className={styles.validationText}>
            {value === true ? "True" : value === false ? "False" : ""}
        </td>
    );
};

const ChitImports = (props) => {
    const { accessToken } = useOidcAccessToken();
    const [loading, setLoading] = React.useState(false);

    const fileInputRef = React.useRef(null);
    const [selectedFile, setSelectedFile] = React.useState(null);

    const [importTypes, setImportTypes] = React.useState([]);
    const [selectedImportType, setSelectedImportType] = React.useState(null);
    const [importData, setImportData] = React.useState([]);
    const [loadingData, setLoadingData] = React.useState(false);

    const clearFileInput = () => {
        setSelectedFile(null);
        setImportData([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);

        if (file && selectedImportType) {
            await previewFileData(file, selectedImportType);
        }

        // Clear the file input so the same file can be selected again
        e.target.value = "";
    };

    const previewFileData = async (file, importTypeId = null) => {
        const useImporterId = importTypeId || selectedImportType;

        if (!file || !useImporterId) {
            return;
        }

        try {
            setLoadingData(true);

            const form = new FormData();
            form.append("File", file);
            form.append("FileName", file.name);
            form.append("Name", file.name);
            form.append("ContentType", file.type || "text/csv");
            form.append("Length", file.size);

            const response = await axios.post(
                `${AccountsURL}/Chits/Import/Data?chitImporterId=${useImporterId}`,
                form,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            setImportData(response.data?.data || []);
        } catch (error) {
            setImportData([]);
        } finally {
            setLoadingData(false);
        }
    };

    const getImportTypes = async () => {
        try {
            const res = await getChitImportTypes();
            return res?.data?.data || [];
        } catch (error) {
            return [];
        }
    };

    React.useEffect(() => {
        const fetchImportTypes = async () => {
            const types = await getImportTypes();
            setImportTypes(types);
            if (types.length > 0) setSelectedImportType(types[0].chitImporterId);
        };

        fetchImportTypes();
    }, []);

    const onFileUpload = async () => {
        if (!selectedFile) {
            toast.warning("Please select a file first");
            return;
        }

        if (!selectedImportType) {
            toast.warning("Please select an import type first");
            return;
        }

        if (importData.length === 0) {
            toast.warning(
                "No data to import. Please check your file and import type."
            );
            return;
        }

        try {
            setLoading(true);
            const fileName = selectedFile.name;
            const form = new FormData();
            form.append("File", selectedFile);

            await axios.post(`${AccountsURL}/Chits/Import?chitImporterId=${selectedImportType}`, form, {
                headers: {
                    type: "file",
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            // Clear file input and state after successful upload
            clearFileInput();

            props.cancelEdit();
            toast.success(`${fileName} has been imported successfully`);
        } catch (error) {
            props.cancelEdit();
        } finally {
            setLoading(false);
        }
    };
    return (
        <Dialog
            title="Import Chits"
            onClose={props.cancelEdit}
            className="dialog-xl"
            height={'680px'}
        >
            <div className="px-5 py-4 d-flex flex-column h-100">
                <div className="mb-4 d-flex gap-4">
                    {/* IMPORT TYPE */}
                    <div className="d-flex flex-column">
                        <Label className="label mb-1">
                            Import Type
                        </Label>
                        <ComboBox
                            data={importTypes}
                            textField="importerName"
                            dataItemKey="chitImporterId"
                            value={importTypes.find(
                                (type) => type.chitImporterId === selectedImportType
                            )}
                            onChange={async (e) => {
                                const importerId = e.value?.chitImporterId;
                                setSelectedImportType(importerId);

                                // If file is already selected, preview the data with new import type
                                if (importerId && selectedFile) {
                                    await previewFileData(selectedFile, importerId);
                                } else {
                                    setImportData([]);
                                }
                            }}
                            style={{ width: "280px" }}
                            placeholder="Select Import Type"
                        />
                    </div>

                    {/* FILE SELECT */}
                    <div className="d-flex flex-column">
                        <Label className="label mb-1">
                            Select File
                        </Label>

                        <div
                            className={`d-flex align-items-center border rounded ${styles.filePicker}`}
                            onClick={() => fileInputRef.current.click()}
                        >
                            {/* LEFT SECTION */}
                            <div
                                className={`flex-grow-1 d-flex align-items-center ${styles.fileContent}`}
                            >
                                {selectedFile ? (
                                    <div
                                        className={`d-flex align-items-center justify-content-between w-100 ${styles.fileDetails}`}
                                    >
                                        <div className="d-flex align-items-center">
                                            <img
                                                src={
                                                    selectedFile.name.endsWith(".csv")
                                                        ? "https://img.icons8.com/color/48/microsoft-excel-2019--v1.png"
                                                        : "https://img.icons8.com/ios-filled/50/document.png"
                                                }
                                                className={styles.fileIcon}
                                                alt="file"
                                            />
                                            <span className={`text-truncate ${styles.fileName}`}>
                                                {selectedFile.name}
                                            </span>
                                        </div>

                                        <span
                                            className={`text-muted ${styles.removeFile}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                clearFileInput();
                                            }}
                                        >
                                            ✕
                                        </span>
                                    </div>
                                ) : (
                                    <span className={`text-muted ${styles.noFileText}`}>
                                        No file selected
                                    </span>
                                )}
                            </div>
                            <div
                                className={`border-start d-flex align-items-center justify-content-center ${styles.filePickerIcon}`}
                            >
                                <RiFileAddFill className={styles.fileIconStyle} />
                            </div>
                        </div>

                        {/* Hidden input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="d-none"
                            accept=".csv,.xlsx,.xls"
                            onChange={handleFileUpload}
                        />
                    </div>
                </div>

                {/* TABLE */}
                <div className={`mb-1 ${styles.gridContainer} position-relative flex-grow-1`}>
                    {loadingData && (
                        <div className={styles.loadingOverlay}>
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                    <Grid
                        style={{ height: "100%" }}
                        data={importData}
                        pageable={false}
                        sortable={true}
                        className="fs-sm"
                        resizable={true}
                        scrollable="scrollable"
                    >
                        <GridColumn
                            field="matterRef"
                            title="Matter"
                            width="100px"
                            className="fs-sm"
                        />
                        <GridColumn
                            field="date"
                            title="Date"
                            cell={DateFormatCell}
                            width="80px"
                        />
                        <GridColumn
                            field="reference"
                            title="Ref"
                            width="100px"
                        />
                        <GridColumn
                            field="details"
                            title="Details"
                            width="200px"
                            className="fs-sm"
                        />
                        <GridColumn
                            field="feeEarnerRef"
                            title="F/E"
                            width="60px"
                        />
                        <GridColumn
                            field="vatAmount"
                            title="VAT"
                            headerClassName="grid-header-center"
                            width="60px"
                            cell={AmountFormatCell}
                        />
                        <GridColumn
                            field="netAmount"
                            title="Net"
                            width="80px"
                            headerClassName="grid-header-center"
                            cell={AmountFormatCell}
                        />
                        <GridColumn
                            field="inValidMessage"
                            title=" "
                            width="120px"
                            cell={ValidationCell}
                        />
                    </Grid>
                </div>
            </div>

            <DialogActionsBar>
                <div className="dialog-footer w-100 d-flex justify-content-end gap-2">
                    <Button
                        className="common-btn bgBtnSecondary"
                        onClick={props.cancelEdit}
                    >
                        Cancel
                    </Button>
                    <SubmitButton
                        className="me-4"
                        onClick={onFileUpload}
                        isLoading={loading}
                        disabled={
                            !selectedFile ||
                            !selectedImportType ||
                            importData.length === 0 ||
                            importData.some(record => record.isValid === false)
                        }
                    >
                        Import
                    </SubmitButton>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
};

export default ChitImports;
